import React, { useState, useEffect } from 'react';
import axios from 'axios';

const StudentEnquiry = ({ enquiryId = null }) => {
    const [formData, setFormData] = useState({
        fullName: '',
        dob: '',
        email: '',
        phone: '',
        choices: [],
        collegeName: ''
    });

    const [wantsBranch, setWantsBranch] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(!!enquiryId);

    useEffect(() => {
        const fetchEnquiryData = async () => {
            if (!enquiryId) return;
            try {
                const response = await axios.get(`http://localhost:8080/ERP/rest/academic/student-enquiry/${enquiryId}`);
                const data = response.data;
                setFormData({
                    fullName: data.fullName || '',
                    dob: data.dob || '',
                    email: data.email || '',
                    phone: data.phone || '',
                    choices: data.choices || [],
                    collegeName: data.collegeName || ''
                });
                if (data.branch) {
                    setWantsBranch('yes');
                } else {
                    setWantsBranch('no');
                }
            } catch (error) {
                console.error("Failed to fetch enquiry data:", error);
                setErrors({ submit: 'Failed to load enquiry data. Please try again.' });
            } finally {
                setIsLoading(false);
            }
        };

        fetchEnquiryData();
    }, [enquiryId]);

    const branchOptions = [
        'Mechanical Engineering',
        'Computer Science',
        'Electrical Engineering',
        'Civil Engineering',
        'Robotics',
        'Biotechnology'
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'phone') {
            const formattedValue = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, [name]: formattedValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleChoiceChange = (choice) => {
        setFormData(prev => {
            const newChoices = prev.choices.includes(choice)
                ? prev.choices.filter(c => c !== choice)
                : [...prev.choices, choice];
            
            if (errors.choices) {
                setErrors(prev => ({ ...prev, choices: null }));
            }
            
            return { ...prev, choices: newChoices };
        });
    };

    const handleBranchDecision = (decision) => {
        setWantsBranch(decision);
        if (decision === 'no') {
            setFormData(prev => ({ ...prev, choices: [] }));
            if (errors.choices) {
                setErrors(prev => ({ ...prev, choices: '' }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.fullName.trim()) newErrors.fullName = 'Please enter your full name';
        
        if (!formData.dob) {
            newErrors.dob = 'Please enter your date of birth';
        } else {
            const birthDate = new Date(formData.dob);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            if (age < 16 || age > 30) {
                newErrors.dob = 'Age must be between 16 and 30 years';
            }
        }

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.email = 'Please enter your email address';
        } else if (!emailPattern.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        const phonePattern = /^[0-9]{10}$/;
        if (!formData.phone.trim()) {
            newErrors.phone = 'Please enter your phone number';
        } else if (!phonePattern.test(formData.phone)) {
            newErrors.phone = 'Please enter a valid 10-digit phone number';
        }

        if (!formData.collegeName.trim()) newErrors.collegeName = 'Please enter your college name';

        if (wantsBranch === 'yes' && formData.choices.length === 0) {
            newErrors.choices = 'Please select at least one branch';
        }

        if (wantsBranch === null) {
            newErrors.branchDecision = 'Please select Yes or No';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        const payload = {
            fullName: formData.fullName,
            dob: formData.dob,
            email: formData.email,
            phone: formData.phone,
            collegeName: formData.collegeName
        };

        if (wantsBranch === 'yes' && formData.choices.length > 0) {
            payload.branch = formData.choices[0];
            payload.choices = formData.choices;
        }
        
        try {
            const response = enquiryId
                ? await axios.put(`http://localhost:8080/ERP/rest/academic/student-enquiry/${enquiryId}`, payload)
                : await axios.post('http://localhost:8080/ERP/rest/academic/student-enquiry', payload);

            if (response.status === 200 || response.status === 201) {
                setShowSuccess(true);
                if (!enquiryId) {
                    setFormData({
                        fullName: '', dob: '', email: '', phone: '', choices: [], collegeName: ''
                    });
                    setWantsBranch(null);
                }
                setTimeout(() => setShowSuccess(false), 5000);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            const errorMessage = error.response?.data?.message || `There was an error ${enquiryId ? 'updating' : 'submitting'} your enquiry.`;
            setErrors({ submit: errorMessage });
        } finally {
            setIsSubmitting(false);
        }
    };

    const today = new Date().toISOString().split('T')[0];

    if (isLoading) {
        return (
            <div className="wiser-template enquiry-page-container">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <div className="wiser-template enquiry-page-container">
            <form onSubmit={handleSubmit} className="enquiry-form">
                <a href="/" className="back-button">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
                    </svg>
                    Back to Home
                </a>
                <div className="form-header animate-on-load">
                    <h1>{enquiryId ? 'Edit Student Enquiry' : 'Student Enquiry Form'}</h1>
                    <p>{enquiryId ? 'Update the student details below.' : 'Please fill out all required fields to submit your enquiry.'}</p>
                </div>

                {showSuccess && (
                    <div className="success-message">
                        ✅ Your enquiry has been {enquiryId ? 'updated' : 'submitted'} successfully!
                    </div>
                )}
                {errors.submit && (
                     <p className="error-message submit-error">{errors.submit}</p>
                )}

                <div className="form-body">
                     <div className="grid-col-2 form-section">
                        <InputField label="Full Name" name="fullName" value={formData.fullName} onChange={handleInputChange} error={errors.fullName} placeholder="Enter your full name"/>
                        <InputField label="Date of Birth" name="dob" type="date" value={formData.dob} onChange={handleInputChange} error={errors.dob} max={today}/>
                        <InputField label="Email Address" name="email" value={formData.email} onChange={handleInputChange} error={errors.email} placeholder="your.email@example.com" disabled={!!enquiryId} />
                        <InputField label="Phone Number" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} error={errors.phone} placeholder="10-digit number"/>
                     </div>
                     <div className="form-section">
                        <InputField label="College Name" name="collegeName" value={formData.collegeName} onChange={handleInputChange} error={errors.collegeName} placeholder="Enter your college name"/>
                     </div>
                     <div className="form-section">
                        <label className="form-label">
                           Do you want to choose a branch? <span className="required-star">*</span>
                        </label>
                        <div className={`radio-group ${errors.branchDecision ? 'error-border' : ''}`}>
                           <RadioOption label="Yes" name="branchDecision" checked={wantsBranch === 'yes'} onChange={() => handleBranchDecision('yes')} />
                           <RadioOption label="No" name="branchDecision" checked={wantsBranch === 'no'} onChange={() => handleBranchDecision('no')} />
                        </div>
                         {errors.branchDecision && <p className="error-message">{errors.branchDecision}</p>}
                     </div>

                     {wantsBranch === 'yes' && (
                        <div className="animate-fade-in form-section">
                           <label className="form-label">
                              Branches <span className="required-star">*</span>
                           </label>
                           <div className={`checkbox-container ${errors.choices ? 'error-border' : ''}`}>
                              {branchOptions.map(option => (
                                 <CheckboxOption
                                    key={option}
                                    label={option}
                                    checked={formData.choices.includes(option)}
                                    onChange={() => handleChoiceChange(option)}
                                 />
                              ))}
                           </div>
                           {errors.choices && <p className="error-message">{errors.choices}</p>}
                        </div>
                     )}

                    <button type="submit" disabled={isSubmitting} className="submit-button form-section">
                        {isSubmitting ? (
                          <div className="spinner-container">
                            <div className="spinner"></div>
                            {enquiryId ? 'Updating...' : 'Submitting...'}
                          </div>
                        ) : (enquiryId ? 'Update Enquiry' : 'Submit Enquiry')}
                    </button>
                </div>
            </form>
            <style>{`
                /* Theme Variables */
                :root {
                    --bg-page: #f9fafb;
                    --bg-card: #ffffff;
                    --border-color: #e5e7eb;
                    --text-primary: #111827;
                    --text-secondary: #6b7280;
                    --accent-color: #4338ca;
                    --accent-color-hover: #3730a3;
                    --error-color: #ef4444;
                    --error-bg: #fee2e2;
                    --success-color: #10b981;
                    --success-bg: #d1fae5;
                }

                .wiser-template {
                    min-height: 100vh;
                    background-color: var(--bg-page);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 1.5rem;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji";
                }
                .enquiry-form {
                    background: var(--bg-card);
                    border-radius: 12px;
                    border: 1px solid var(--border-color);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.07), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
                    padding: 2.5rem;
                    max-width: 650px;
                    width: 100%;
                    position: relative;
                    transition: transform 0.3s ease-out, box-shadow 0.3s ease-out;
                }
                .enquiry-form:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                }
                
                .back-button {
                    position: absolute;
                    top: 1.5rem;
                    left: 1.5rem;
                    text-decoration: none;
                    color: var(--text-secondary);
                    font-weight: 500;
                    font-size: 0.875rem;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 0.75rem;
                    border-radius: 6px;
                    background-color: transparent;
                    border: 1px solid var(--border-color);
                    transition: all 0.2s ease-in-out;
                }
                .back-button:hover {
                    background-color: #f3f4f6;
                    color: var(--text-primary);
                    transform: translateX(-2px);
                }

                .form-header {
                    text-align: center;
                    margin-bottom: 2.5rem;
                }
                .form-header h1 {
                    color: var(--text-primary);
                    font-size: 2rem;
                    font-weight: 700;
                    margin-bottom: 0.5rem;
                }
                .form-header p {
                    color: var(--text-secondary);
                    font-size: 1rem;
                }

                .success-message, .submit-error {
                    padding: 1rem;
                    border-radius: 8px;
                    margin-bottom: 1.5rem;
                    text-align: center;
                    font-weight: 500;
                    animation: slideDown 0.5s ease-out forwards;
                }
                .success-message {
                    background-color: var(--success-bg);
                    color: #065f46;
                    border: 1px solid #6ee7b7;
                }
                .submit-error {
                    background-color: var(--error-bg);
                    border: 1px solid #fca5a5;
                    color: #991b1b;
                }
                @keyframes slideDown { from { transform: translateY(-15px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

                .form-body {
                    display: flex;
                    flex-direction: column;
                    gap: 1.75rem;
                }
                
                .animate-on-load, .form-section { 
                    animation: fadeInStagger 0.5s ease-out forwards; 
                    opacity: 0; 
                }
                .grid-col-2 {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 1.5rem;
                }
                /* Staggered animation delays */
                .form-header.animate-on-load { animation-delay: 0.1s; }
                .form-body .grid-col-2 { animation-delay: 0.2s; }
                .form-body > div:nth-child(2) { animation-delay: 0.3s; }
                .form-body > div:nth-child(3) { animation-delay: 0.4s; }
                .form-body > div:nth-child(4) { animation-delay: 0.5s; }
                .submit-button { animation-delay: 0.6s; }

                @keyframes fadeInStagger {
                    from { opacity: 0; transform: translateY(15px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .form-label {
                    display: block;
                    font-weight: 500;
                    color: #374151;
                    margin-bottom: 0.5rem;
                    font-size: 0.875rem;
                }
                .required-star { color: var(--accent-color); }

                .form-input {
                    width: 100%;
                    padding: 0.75rem 1rem;
                    border: 1px solid var(--border-color);
                    border-radius: 6px;
                    font-size: 1rem;
                    transition: border-color 0.2s, box-shadow 0.2s;
                    outline: none;
                    background-color: #fff;
                    color: var(--text-primary);
                }
                .form-input:disabled {
                    background-color: #f3f4f6;
                    cursor: not-allowed;
                }
                .form-input:focus {
                    border-color: var(--accent-color);
                    box-shadow: 0 0 0 3px rgba(67, 56, 202, 0.2);
                }
                .form-input.error { border-color: var(--error-color); }
                .form-input.error:focus { box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2); }
                .error-message {
                    color: var(--error-color);
                    font-size: 0.8rem;
                    margin-top: 0.25rem;
                }

                .radio-group {
                    display: flex;
                    gap: 1rem;
                    border: 2px solid transparent;
                    border-radius: 8px;
                    padding: 0.25rem;
                }
                .radio-group.error-border { border-color: var(--error-color); }
                
                .animate-fade-in { animation: slideDown 0.5s ease-out forwards; }
                
                .checkbox-container {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 0.75rem;
                    border: 1px solid var(--border-color);
                    border-radius: 8px;
                    padding: 1rem;
                }
                .checkbox-container.error-border { border-color: var(--error-color); }

                .submit-button {
                    width: 100%;
                    background-color: var(--accent-color);
                    color: #fff;
                    border: none;
                    padding: 1rem 1.5rem;
                    border-radius: 8px;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                }
                .submit-button:hover:not(:disabled) {
                    background-color: var(--accent-color-hover);
                    transform: translateY(-2px);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
                }
                .submit-button:active:not(:disabled) { transform: translateY(0); }
                .submit-button:disabled {
                    background-color: #d1d5db;
                    cursor: not-allowed;
                }
                .loading-spinner {
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #4338ca;
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;
                    animation: spin 1s linear infinite;
                }

                .spinner-container {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .spinner {
                    display: inline-block;
                    width: 1.25rem;
                    height: 1.25rem;
                    border: 3px solid rgba(255,255,255,0.3);
                    border-radius: 50%;
                    border-top-color: #fff;
                    animation: spin 1s ease-in-out infinite;
                    margin-right: 0.75rem;
                }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

const InputField = ({ label, name, type = 'text', value, onChange, error, ...props }) => (
    <div>
        <label htmlFor={name} className="form-label">
            {label} <span className="required-star">*</span>
        </label>
        <input id={name} type={type} name={name} value={value} onChange={onChange} className={`form-input ${error ? 'error' : ''}`} {...props} />
        {error && <p className="error-message">{error}</p>}
    </div>
);

const RadioOption = ({ label, name, checked, onChange }) => (
   <label className="radio-label">
      <input type="radio" name={name} checked={checked} onChange={onChange} />
      <span>{label}</span>
      <style>{`
        .radio-label {
            display: flex;
            align-items: center;
            cursor: pointer;
            padding: 0.5rem 1rem;
            border-radius: 6px;
            transition: all 0.2s;
            border: 1px solid var(--border-color);
            background-color: ${checked ? 'var(--accent-color)' : 'transparent'};
            color: ${checked ? '#fff' : 'var(--text-secondary)'};
        }
        .radio-label:hover {
            border-color: ${checked ? 'var(--accent-color)' : 'var(--accent-color-hover)'};
        }
        .radio-label input {
            display: none;
        }
        .radio-label span {
            font-weight: 500;
        }
      `}</style>
   </label>
);

const CheckboxOption = ({ label, checked, onChange }) => (
    <label className="checkbox-label">
        <input type="checkbox" checked={checked} onChange={onChange} />
        <span className="checkbox-custom">
            {checked && <svg viewBox="0 0 16 16" fill="white"><path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z"/></svg>}
        </span>
        <span>{label}</span>
        <style>{`
            .checkbox-label {
                display: flex;
                align-items: center;
                cursor: pointer;
                padding: 0.5rem;
                border-radius: 6px;
                transition: background-color 0.2s;
                user-select: none;
            }
            .checkbox-label:hover {
                background-color: #f3f4f6;
            }
            .checkbox-label input {
                display: none;
            }
            .checkbox-label span {
                color: var(--text-secondary);
                font-weight: 500;
            }
            .checkbox-custom {
                width: 1.25rem;
                height: 1.25rem;
                border: 2px solid var(--border-color);
                border-radius: 4px;
                margin-right: 0.75rem;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s;
                flex-shrink: 0;
            }
            .checkbox-label input:checked + .checkbox-custom {
                background-color: var(--accent-color);
                border-color: var(--accent-color);
            }
            .checkbox-label:hover .checkbox-custom {
                border-color: var(--accent-color);
            }
        `}</style>
    </label>
);

export default StudentEnquiry;

